import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { updateMe } from "@/lib/api";
import {
  User,
  Linkedin,
  MapPin,
  Phone,
  Briefcase,
  Star,
  CheckCircle2,
  Loader2,
  ChevronRight,
  Sparkles,
} from "lucide-react";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [linkedinUrl, setLinkedinUrl] = useState(user?.linkedin_url || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const handleSaveLinkedIn = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateMe({ linkedin_url: linkedinUrl.trim() });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const linkedinScore = user?.linkedin_score ?? null;
  const skills = user?.skills || [];
  const firstName = (user?.name || "").split(" ")[0] || "there";

  // Academics (campus students)
  const [acad, setAcad] = useState({
    branch: user?.branch || "",
    section: user?.section || "",
    cgpa: user?.cgpa ?? "",
    grad_year: user?.grad_year ?? "",
    pct_10: user?.pct_10 ?? "",
    pct_12: user?.pct_12 ?? "",
    active_backlogs: user?.active_backlogs ?? "",
    education_gap_years: user?.education_gap_years ?? "",
  });
  const [acadSaving, setAcadSaving] = useState(false);
  const [acadMsg, setAcadMsg] = useState(null);
  const cgpaChanged = String(acad.cgpa) !== String(user?.cgpa ?? "");

  const handleSaveAcademics = async (e) => {
    e.preventDefault();
    setAcadSaving(true);
    setAcadMsg(null);
    try {
      const patch = {};
      if (acad.branch.trim()) patch.branch = acad.branch.trim();
      if (acad.section.trim()) patch.section = acad.section.trim().toUpperCase();
      if (acad.cgpa !== "") patch.cgpa = Number(acad.cgpa);
      if (acad.grad_year !== "") patch.grad_year = Number(acad.grad_year);
      if (acad.pct_10 !== "") patch.pct_10 = Number(acad.pct_10);
      if (acad.pct_12 !== "") patch.pct_12 = Number(acad.pct_12);
      if (acad.active_backlogs !== "") patch.active_backlogs = Number(acad.active_backlogs);
      if (acad.education_gap_years !== "") patch.education_gap_years = Number(acad.education_gap_years);
      await updateMe(patch);
      await refresh();
      setAcadMsg({ ok: true, text: cgpaChanged && user?.cgpa != null ? "Saved. Your CGPA is now pending re-verification by your placement cell." : "Saved." });
    } catch (err) {
      setAcadMsg({ ok: false, text: err?.response?.data?.detail?.[0]?.msg || "Failed to save. Check the values (CGPA 0–10)." });
    } finally {
      setAcadSaving(false);
    }
  };

  return (
    <div className="container-x py-10 md:py-14">
      {/* Header */}
      <div className="mb-10">
        <span className="overline">Your profile</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
          Hi {firstName},{" "}
          <span className="italic text-stone-500">this is your Wishire profile.</span>
        </h1>
        <p className="mt-3 text-stone-500 max-w-xl leading-relaxed">
          Keep your profile up to date so recruiters and the Wishire Score engine get the full picture.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — identity card */}
        <div className="lg:col-span-1 space-y-6">
          {/* Avatar + basics */}
          <div className="rounded-3xl bg-stone-900 text-stone-50 p-8 wsh-grain relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                background:
                  "radial-gradient(400px 300px at 80% 20%, rgba(217,119,6,0.5), transparent 60%)",
              }}
            />
            <div className="relative">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user?.name || "Profile photo"}
                  data-testid="profile-avatar-img"
                  referrerPolicy="no-referrer"
                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "flex"; }}
                  className="w-16 h-16 rounded-full object-cover mb-5 ring-2 ring-amber-300/40"
                />
              ) : null}
              <div
                className="w-16 h-16 rounded-full bg-amber-300 text-stone-900 font-display text-2xl items-center justify-center mb-5"
                style={{ display: user?.picture ? "none" : "flex" }}
              >
                {(user?.name || "?")[0].toUpperCase()}
              </div>
              <p className="font-display text-2xl text-stone-50">{user?.name}</p>
              {user?.headline && (
                <p className="mt-1 text-stone-300 text-sm leading-relaxed">{user.headline}</p>
              )}

              <div className="mt-5 space-y-2 text-sm">
                {user?.city && (
                  <div className="flex items-center gap-2 text-stone-300">
                    <MapPin size={14} /> <span>{user.city}</span>
                  </div>
                )}
                {user?.phone && (
                  <div className="flex items-center gap-2 text-stone-300">
                    <Phone size={14} /> <span>{user.phone}</span>
                  </div>
                )}
                {user?.years_experience != null && (
                  <div className="flex items-center gap-2 text-stone-300">
                    <Briefcase size={14} />{" "}
                    <span>{user.years_experience} yr{user.years_experience !== 1 ? "s" : ""} experience</span>
                  </div>
                )}
                {user?.email && (
                  <div className="flex items-center gap-2 text-stone-300">
                    <User size={14} /> <span className="truncate">{user.email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* LinkedIn score card */}
          {linkedinScore != null ? (
            <div className="rounded-3xl bg-[#F5F3ED] border border-stone-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Linkedin size={16} className="text-[#0A66C2]" />
                <span className="overline">LinkedIn score</span>
              </div>
              <p className="font-display text-5xl">
                {linkedinScore}
                <span className="text-stone-400 text-2xl"> / 100</span>
              </p>
              <div className="mt-3 h-2 rounded-full bg-stone-200 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${Math.min(100, linkedinScore)}%`,
                    background: linkedinScore >= 70 ? "#16a34a" : linkedinScore >= 45 ? "#d97706" : "#dc2626",
                  }}
                />
              </div>
              <p className="mt-2 text-xs text-stone-500 font-mono uppercase tracking-widest">
                {linkedinScore >= 70 ? "Strong" : linkedinScore >= 45 ? "Average" : "Needs work"}
              </p>
            </div>
          ) : (
            <div className="rounded-3xl border-2 border-dashed border-stone-200 p-6 text-center">
              <Linkedin size={24} className="mx-auto text-stone-300 mb-3" />
              <p className="text-sm text-stone-500">Add your LinkedIn URL to get your LinkedIn score</p>
            </div>
          )}
        </div>

        {/* Right — editable details */}
        <div className="lg:col-span-2 space-y-6">
          {/* LinkedIn URL form */}
          <div className="rounded-3xl border border-stone-200 bg-white p-8">
            <div className="flex items-center gap-2 mb-1">
              <Linkedin size={18} className="text-[#0A66C2]" />
              <h2 className="font-display text-xl">LinkedIn profile</h2>
            </div>
            <p className="text-sm text-stone-500 mb-5 ml-7">
              Paste your LinkedIn profile URL. We'll fetch and analyse your profile automatically.
            </p>
            <form onSubmit={handleSaveLinkedIn} className="flex gap-3">
              <input
                type="url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://www.linkedin.com/in/yourhandle"
                className="flex-1 px-4 py-3 rounded-full bg-[#FDFBF7] border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm text-stone-900"
              />
              <button
                type="submit"
                disabled={saving || !linkedinUrl.trim()}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60 text-sm font-medium whitespace-nowrap"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                {saving ? "Saving…" : saved ? "Saved ✓" : "Save & analyse"}
                {!saving && !saved && <ChevronRight size={14} />}
              </button>
            </form>
            {error && <p className="mt-2 text-sm text-red-600 ml-2">{error}</p>}
            {saved && (
              <p className="mt-2 text-sm text-emerald-600 ml-2 flex items-center gap-1">
                <CheckCircle2 size={14} /> LinkedIn saved — analysis running in background. Check back in a minute.
              </p>
            )}
          </div>

          {/* Academics — editable, feeds drive eligibility */}
          <div className="rounded-3xl border border-stone-200 bg-white p-8">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase size={18} className="text-[#15803D]" />
              <h2 className="font-display text-xl">Academics</h2>
              {user?.cgpa_verified && (
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  <CheckCircle2 size={11} /> CGPA verified
                </span>
              )}
            </div>
            <p className="text-sm text-stone-500 mb-5 ml-7">
              Used to check your eligibility for campus drives. Editing your CGPA sends it back for re-verification by your placement cell.
            </p>
            <form onSubmit={handleSaveAcademics} className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
              <div>
                <label className="overline">Course / Branch</label>
                <datalist id="wishire-courses">
                  {["B.Tech CSE", "B.Tech IT", "B.Tech ECE", "B.Tech EE", "B.Tech Mechanical", "B.Tech Civil", "BCA", "MCA", "B.Sc Computer Science", "B.Sc IT", "B.Sc", "BBA", "MBA", "PGDM", "B.Com", "B.Com (Hons)", "M.Com", "BMS", "BA", "BA (Hons) Economics", "MA", "B.Ed", "B.Pharm", "BHM (Hotel Management)", "LLB"].map((c) => <option key={c} value={c} />)}
                </datalist>
                <input list="wishire-courses" value={acad.branch} onChange={(e) => setAcad((a) => ({ ...a, branch: e.target.value }))} data-testid="profile-branch"
                  placeholder="e.g. BBA, B.Com, B.Tech CSE" className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
              </div>
              <div>
                <label className="overline">Section</label>
                <input value={acad.section} maxLength={20} onChange={(e) => setAcad((a) => ({ ...a, section: e.target.value }))} data-testid="profile-section"
                  placeholder="A" className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
              </div>
              <div>
                <label className="overline">CGPA</label>
                <input type="number" step="0.01" min="0" max="10" value={acad.cgpa} onChange={(e) => setAcad((a) => ({ ...a, cgpa: e.target.value }))} data-testid="profile-cgpa"
                  placeholder="8.2" className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-stone-300 text-sm font-mono focus:outline-none focus:border-stone-900" />
              </div>
              <div>
                <label className="overline">Grad year</label>
                <input type="number" min="1990" max="2100" value={acad.grad_year} onChange={(e) => setAcad((a) => ({ ...a, grad_year: e.target.value }))} data-testid="profile-gradyear"
                  placeholder="2026" className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-stone-300 text-sm font-mono focus:outline-none focus:border-stone-900" />
              </div>
              <div className="sm:col-span-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["pct_10", "10th %", "e.g. 78"], ["pct_12", "12th %", "e.g. 74"], ["active_backlogs", "Active backlogs", "0"], ["education_gap_years", "Education gap (years)", "0"]].map(([k, l, ph]) => (
                  <div key={k}>
                    <label className="overline">{l}</label>
                    <input type="number" step="any" min="0" value={acad[k]} onChange={(e) => setAcad((a) => ({ ...a, [k]: e.target.value }))} data-testid={`profile-${k}`}
                      placeholder={ph} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-stone-300 text-sm font-mono focus:outline-none focus:border-stone-900" />
                  </div>
                ))}
              </div>
              <p className="sm:col-span-5 -mt-1 text-xs text-stone-500">10th, 12th, backlogs and gap are what companies like TCS and Infosys check in their published criteria. Fill once; every company page uses them.</p>
              <button type="submit" disabled={acadSaving} data-testid="profile-acad-save"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60 text-sm font-medium">
                {acadSaving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
              </button>
            </form>
            {acadMsg && (
              <p className={`mt-3 text-sm ${acadMsg.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="profile-acad-msg">{acadMsg.text}</p>
            )}
          </div>

          {/* Skills */}
          <div className="rounded-3xl border border-stone-200 bg-white p-8">
            <div className="flex items-center gap-2 mb-5">
              <Star size={18} className="text-amber-500" />
              <h2 className="font-display text-xl">Skills</h2>
            </div>
            {skills.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {skills.map((s) => (
                  <span
                    key={s}
                    className="px-3 py-1.5 rounded-full bg-[#F5F3ED] border border-stone-200 text-sm text-stone-700 font-medium"
                  >
                    {s}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-stone-400 text-sm">
                No skills yet — they'll be populated automatically after we analyse your LinkedIn profile or resume.
              </p>
            )}
          </div>

          {/* LinkedIn enriched details */}
          {(user?.headline || user?.city || user?.years_experience != null || user?.phone) && (
            <div className="rounded-3xl border border-stone-200 bg-white p-8">
              <div className="flex items-center gap-2 mb-5">
                <Sparkles size={18} className="text-[#C2410C]" />
                <h2 className="font-display text-xl">Profile details</h2>
                <span className="ml-auto text-xs text-stone-400 font-mono uppercase tracking-widest">from LinkedIn</span>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {user?.headline && <Detail label="Headline" value={user.headline} />}
                {user?.city && <Detail label="City" value={user.city} />}
                {user?.phone && <Detail label="Phone" value={user.phone} />}
                {user?.years_experience != null && (
                  <Detail label="Experience" value={`${user.years_experience} year${user.years_experience !== 1 ? "s" : ""}`} />
                )}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-stone-400 font-mono uppercase tracking-widest mb-1">{label}</dt>
      <dd className="text-stone-800 text-sm leading-relaxed">{value}</dd>
    </div>
  );
}
